import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/state/AppContext";
import { api, apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { OtpInput } from "@/components/OtpInput";
import { ProfileForm } from "@/components/ProfileForm";
import { roleHome } from "@/lib/roleHome";

export default function VerifyPhone() {
  const { t, auth, setSession, refreshCart } = useApp();
  const navigate = useNavigate();
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState("");
  const [requestId, setRequestId] = useState("");
  const [normalizedPhone, setNormalizedPhone] = useState("");
  const [landing, setLanding] = useState("/account");
  const [loading, setLoading] = useState(false);
  const [otpError, setOtpError] = useState("");

  const sendOtp = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/phone/request", { phone });
      setRequestId(data.request_id);
      setNormalizedPhone(data.phone);
      setDevCode(data.dev_code || "");
      setStep("otp");
    } catch (e) {
      toast.error(apiErrorMessage(e, t("errors.generic")));
    } finally {
      setLoading(false);
    }
  };

  const verify = async () => {
    setLoading(true);
    setOtpError("");
    try {
      const { data } = await api.post("/auth/phone/verify", { request_id: requestId, code });
      setSession(data);
      await refreshCart();
      toast.success(t("auth.phoneLinked"));
      const home = roleHome[data.active_role_type] || "/account";
      setLanding(home);
      if (data.identity?.profile_completed) navigate(home, { replace: true });
      else setStep("profile");
    } catch (e) {
      const status = e?.response?.status;
      const rejected = status >= 400 && status < 500 && status !== 429;
      setOtpError(rejected ? t("auth.otpWrong") : apiErrorMessage(e, t("errors.generic")));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto flex max-w-md flex-col px-5 py-16 sm:px-0" data-testid="verify-phone-page">
      <h1 className="font-heading text-3xl font-bold tracking-tight">{t("auth.phoneStepTitle")}</h1>
      <p className="mt-3 text-sm text-muted-foreground">
        {t("auth.phoneStepSubtitle", { email: auth?.identity?.email || "" })}
      </p>

      {step === "profile" ? (
        <div className="mt-8 space-y-5" data-testid="verify-phone-profile-step">
          <h2 className="font-heading text-xl font-semibold">{t("profile.setupTitle")}</h2>
          <ProfileForm onSaved={() => navigate(landing, { replace: true })} onSkip={() => navigate(landing, { replace: true })} />
        </div>
      ) : step === "phone" ? (
        <div className="mt-8 space-y-5" data-testid="verify-phone-step">
          <div className="space-y-2">
            <Label htmlFor="attach-phone">{t("auth.phoneLabel")}</Label>
            <Input
              id="attach-phone"
              data-testid="attach-phone-input"
              value={phone}
              type="tel"
              inputMode="numeric"
              autoComplete="tel"
              placeholder={t("auth.phonePlaceholder")}
              onChange={(e) => setPhone(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendOtp()}
            />
          </div>
          <Button
            className="w-full rounded-full"
            data-testid="attach-phone-send-otp-btn"
            disabled={loading || !phone}
            onClick={sendOtp}
          >
            {loading ? t("common.loading") : t("auth.sendOtp")}
          </Button>
        </div>
      ) : (
        <div className="mt-8 space-y-5" data-testid="verify-phone-otp-step">
          <p className="text-sm text-muted-foreground">{t("auth.otpSubtitle", { phone: normalizedPhone })}</p>
          {devCode && (
            <div
              data-testid="attach-phone-dev-otp-notice"
              className="flex items-center gap-2 rounded-xl border border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/10 px-4 py-3 text-sm"
            >
              <ShieldCheck size={16} className="text-[hsl(var(--accent))]" />
              {t("auth.devNotice", { code: devCode })}
            </div>
          )}
          <OtpInput
            id="attach-otp"
            testId="attach-phone-otp-input"
            errorTestId="attach-phone-otp-error"
            label={t("auth.otpLabel")}
            value={code}
            onChange={setCode}
            onEdit={() => setOtpError("")}
            onSubmit={() => code.length === 6 && verify()}
            error={otpError}
          />
          <Button
            className="w-full rounded-full"
            data-testid="attach-phone-verify-btn"
            disabled={loading || code.length < 6}
            onClick={verify}
          >
            {loading ? t("common.loading") : t("auth.verify")}
          </Button>
          <button
            className="flex items-center gap-1 text-sm text-muted-foreground"
            data-testid="attach-phone-change-btn"
            onClick={() => setStep("phone")}
          >
            <ArrowLeft size={14} /> {t("auth.changeNumber")}
          </button>
        </div>
      )}
    </div>
  );
}
